import { useEffect } from "react";
import { t } from "i18next";
import { useHistory } from "react-router-dom";
import DataGrid from "../../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import ApiService from "../../../core/service/ApiService";
import Helper from "../../../core/Helper";
import { Avatar, Button, Tag, Typography } from "antd";
import {
    UserOutlined,
    IdcardOutlined,
    PhoneOutlined,
    CalendarOutlined,
    CheckCircleFilled,
    ExclamationCircleFilled,
    ExportOutlined
} from "@ant-design/icons";

const { Text } = Typography;

export default function PatientGrid() {
    const history = useHistory();
    const { queryModel, setDataGridProps, setData, setPaginationResult, refreshDataGrid } = useDataGrid();


    const birthDate = (d, m, y) => {
        if (d)
            return `${d}/${m}/${y}`

        return '-'
    }

    const openPatientResults = async (row) => {
        const res = await ApiService.Patient.GetToken(row.id);
        if (res.success) {
            const url = `${window.AppSettings.server.result}?access_token=${res.data}`
            window.open(url, '_blank')
        }
    }

    // --- MOBİL KART RENDER ---
    const renderCard = (row) => {
        const fullName = `${row.name || ''} ${row.lastname || ''}`.trim();
        const phone = row.phone1 || '-';
        const birth = birthDate(row.birthDay, row.birthMonth, row.birthYear);
        const genderText = row.gender ? t(row.gender) : '-';
        const balance = row.balance;
        const hasDebt = balance && balance > 0;
        const isPositive = balance && balance < 0;

        return (
            <div>
                {/* ÜST: Avatar + Ad/Soyad + Validate badge */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                    <Avatar size={36} icon={<UserOutlined />} style={{ backgroundColor: row.validate ? '#1890ff' : '#bfbfbf', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <a
                                href={`/app/patients/${row.id}/appointments`}
                                target="_blank"
                                style={{ fontWeight: 600, fontSize: 14, color: '#262626' }}
                            >
                                {fullName || '-'}
                            </a>
                            {row.validate ? (
                                <CheckCircleFilled style={{ color: '#52c41a', fontSize: 14 }} />
                            ) : (
                                <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 14 }} />
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                            <IdcardOutlined />
                            <span>{row.customId || '-'}</span>
                        </div>
                    </div>
                </div>

                {/* ORTA: Telefon / Doğum / Cinsiyet — kompakt grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 6,
                    marginBottom: 10,
                    fontSize: 12,
                    color: '#595959'
                }}>
                    {phone !== '-' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <PhoneOutlined style={{ color: '#8c8c8c' }} />
                            <span>{phone}</span>
                        </div>
                    )}
                    {birth !== '-' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CalendarOutlined style={{ color: '#8c8c8c' }} />
                            <span>{birth}</span>
                        </div>
                    )}
                    {row.gender && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <UserOutlined style={{ color: '#8c8c8c' }} />
                            <span>{genderText}</span>
                        </div>
                    )}
                    {(row.motherName || row.fatherName) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, gridColumn: 'span 2' }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Anne/Baba: {row.motherName || '-'} / {row.fatherName || '-'}
                            </Text>
                        </div>
                    )}
                </div>

                {/* ALT: Bakiye + Patient Results */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: '1px dashed #f0f0f0',
                    paddingTop: 8,
                    gap: 8
                }}>
                    <div>
                        <Text style={{ fontSize: 10, color: '#8c8c8c' }}>{t('Balance')}: </Text>
                        <Text strong style={{
                            fontSize: 13,
                            color: hasDebt ? '#cf1322' : (isPositive ? '#389e0d' : '#595959')
                        }}>
                            {balance != null ? Helper.Currency.format(balance) : '-'}
                        </Text>
                    </div>
                    <Button
                        size="small"
                        icon={<ExportOutlined />}
                        disabled={!row?.validate}
                        onClick={() => openPatientResults(row)}
                    >
                        {t('Patient Results')}
                    </Button>
                </div>
            </div>
        );
    };

    useEffect(() => {
        setDataGridProps({
            rowKey: 'id',
            cardRender: renderCard,
            columns: [
                { dataIndex: 'customId', key: 'customId', title: t('Patient Id'), sorter: true, render: (text, row) => text || '-' },
                { dataIndex: 'name', key: 'name', title: t('Name'), sorter: true, render: (text, row) => <a target={'_blank'} href={(`/app/patients/${row.id}/appointments`)}>{text}</a> },
                { dataIndex: 'lastname', key: 'lastname', title: t('Lastname'), sorter: true },
                { dataIndex: 'motherName', key: 'motherName', title: t('Mother Name'), sorter: true, render: (text) => text || '-' },
                { dataIndex: 'fatherName', key: 'fatherName', title: t('Father Name'), sorter: true, render: (text) => text || '-' },
                { dataIndex: 'birthdate', key: 'birthdate', title: t('Birthdate'), render: (text, row) => birthDate(row.birthDay, row.birthMonth, row.birthYear) },
                { dataIndex: 'gender', key: 'gender', title: t('Gender'), sorter: true, render: (text, row) => t(text || '-') },
                { dataIndex: 'phone1', key: 'phone1', title: t('Phone') },
                { dataIndex: 'balance', key: 'balance', title: t('Balance'), sorter: true, render: (text, row) => t(Helper.Currency.format(text) || '-') },
                {
                    dataIndex: '#', key: '#', title: t('#'), sorter: true, render: (text, row) => {
                        return (
                            <Button
                                disabled={!row?.validate}
                                size={'small'}
                                onClick={() => openPatientResults(row)}
                            >
                                {t('Patient Results')}
                            </Button>
                        )
                    }
                },
            ]
        })
    }, [])

    const load = async () => {
        const res = await ApiService.Patient.GetPatientDataGrid(queryModel);
        if (res.success) {
            setData(res.data)
            setPaginationResult(res.pagination);
        }
    }

    useEffect(() => {
        load()
    }, [queryModel.updateKey])

    return (
        <>
            <DataGrid />
        </>
    )
}
